import { useEffect, useRef } from 'react';

/** Observe actual buttons, not a mounted card that may still be below the fold. */
export function useReviewCTAVisibility(
  providersKey: string,
  pending: boolean,
  onVisible?: (provider: string) => Promise<void>,
) {
  const ref = useRef<HTMLDivElement>(null);
  const callback = useRef(onVisible);
  callback.current = onVisible;
  const reported = useRef(new Set<string>());
  useEffect(() => {
    if (!onVisible || pending || !ref.current || typeof IntersectionObserver === 'undefined') return;
    let disposed = false;
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();
    const visible = new Set<Element>();
    const attempts = new Map<string, number>();
    const inFlight = new Set<string>();
    const cancel = (element: Element) => {
      clearTimeout(timers.get(element));
      timers.delete(element);
    };
    const schedule = (element: Element) => {
      const provider = element.getAttribute('data-review-provider');
      if (!provider || document.visibilityState !== 'visible' || reported.current.has(provider) || inFlight.has(provider) || timers.has(element) || (attempts.get(provider) ?? 0) >= 3) return;
      timers.set(element, setTimeout(async () => {
        timers.delete(element);
        if (disposed || !visible.has(element) || document.visibilityState !== 'visible') return;
        inFlight.add(provider);
        attempts.set(provider, (attempts.get(provider) ?? 0) + 1);
        try {
          await callback.current?.(provider);
          if (!disposed) reported.current.add(provider);
        } catch {
          // Telemetry must never interrupt a review or block the survey.
        } finally {
          inFlight.delete(provider);
          if (!disposed && visible.has(element)) schedule(element);
        }
      }, 1000));
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          visible.add(entry.target);
          schedule(entry.target);
        } else {
          visible.delete(entry.target);
          cancel(entry.target);
        }
      }
    }, { threshold: [0, 0.5] });
    ref.current.querySelectorAll('[data-review-provider]').forEach(element => observer.observe(element));
    const visibilityChanged = () => {
      timers.forEach((_, element) => cancel(element));
      if (document.visibilityState === 'visible') visible.forEach(schedule);
    };
    document.addEventListener('visibilitychange', visibilityChanged);
    return () => {
      disposed = true;
      observer.disconnect();
      timers.forEach((_, element) => cancel(element));
      document.removeEventListener('visibilitychange', visibilityChanged);
    };
  }, [providersKey, pending, Boolean(onVisible)]);
  return ref;
}
